import { Application, BasePlugin } from '@goodboydigital/astro';
import firebase from 'firebase/app';
require('firebase/firestore');
require('firebase/storage');

import { App, Shared } from '../App';
import { Settings } from '../AppConfig';
import { ReplayData } from '../game/replay/ReplaySystem';
import { Browser } from '../utils/Browser';
import { Random } from '../utils/Random';

const pako = require('pako');

/** Interface for entries in database */
export interface ReplayEntry
{
    systemVersion: number
    levelHash: string,
    replayHash: string,
    replayTime: number,
    timestamp?: string,
}

/** Type of binary replay buffer */
export type ReplayBinaryData = Uint8Array;

// Max amount of entries to be fetch at single time
const MAX_REPLAY_ENTRIES_FETCH = 32;

// Location of static databases
const STATIC_ENDPOINT = `https://dev.goodboydigital.com/client/turner/cn-toon-trails`;
const LOCAL_STATIC_ENDPOINT = `assets`;
// The game looks for data by level version, which is not the same thing as the app version.
// If LEVEL_SYSTEM_VERSION is 6, then the API will fetch static data like this:
// STATIC_ENDPOINT/database-v6/entries.json - to see all ghost data available
// STATIC_ENDPOINT/database-v6/<replay-hash>.jsz - to download a specific ghost

export class APIPlugin implements BasePlugin
{
    public app: App;
    public useFirebase = false;
    public useLocal = true;
    private firebaseInitialised = false;

    constructor(app: Application)
    {
        this.app = app as App;
        this.useFirebase = Settings.rec;
    }

    /**
     * Fetch replay entries for a level, limited by max value.
     * @param levelHash - Unique level hash
     * @param max - Mas number of players
     * @returns
     */
    public async fetchLevelReplayEntries(levelHash: string, max = MAX_REPLAY_ENTRIES_FETCH): Promise<ReplayEntry[]>
    {
        if (this.useFirebase) return this.firebaseFetchLevelReplayEntries(levelHash, max);

        return this.staticFetchLevelReplayEntries(levelHash, max);
    }

    /**
     * Fetch replay data file from given replay hash
     * @param replayHash - Unique replay hash
     * @returns
     */
    public async fetchLevelReplayFile(replayHash: string): Promise<ReplayData | null>
    {
        if (this.useFirebase) return this.firebaseFetchLevelReplayFile(replayHash);

        return this.staticFetchLevelReplayFile(replayHash);
    }

    /**
     * Submit a replay to the API
     * @param entry - The replay info
     * @param data - Actual replay data, in binary format
     * @returns
     */
    public async submitReplay(entry: ReplayEntry, data: ReplayBinaryData): Promise<void>
    {
        if (this.useFirebase) return this.firebaseSubmitReplay(entry, data);

        return Promise.resolve();
    }

    // === STATIC DATABASE ============================================================================================

    private async staticFetchLevelReplayEntries(levelHash: string, max: number): Promise<ReplayEntry[]>
    {
        if (max > MAX_REPLAY_ENTRIES_FETCH) max = MAX_REPLAY_ENTRIES_FETCH;
        
        //console.log("Fetch Replay Entries, LevelHash: ", levelHash);

        const systemVersion = Shared.game.level.LEVEL_SYSTEM_VERSION;
        const database = this.useLocal ? `local-database-v${systemVersion}` : `database-v${systemVersion}`;
        const jsonUrl = this.useLocal ? `${LOCAL_STATIC_ENDPOINT}/${database}/entries.json?${Date.now()}` : `${STATIC_ENDPOINT}/${database}/entries.json?${Date.now()}`; // Prevent cache

        const allEntries = await Browser.loadJson<ReplayEntry[]>(jsonUrl);

        // Return empty array if entries fetch fail
        if (!allEntries || !allEntries.length) return [];
        
        let levelEntries = allEntries.filter((entry) => entry.levelHash === levelHash);

        levelEntries = this.filterUniqueReplays(levelEntries);
        levelEntries = Random.shuffle(levelEntries);
        if (levelEntries.length > max)levelEntries.length = max;
        return levelEntries;
    }

    private async staticFetchLevelReplayFile(replayHash: string): Promise<ReplayData | null>
    {
        try
        {
            const systemVersion = Shared.game.level.LEVEL_SYSTEM_VERSION;
            const database = this.useLocal ? `local-database-v${systemVersion}` : `database-v${systemVersion}`;
            const url = this.useLocal ? `${LOCAL_STATIC_ENDPOINT}/${database}/${replayHash}.jsz` : `${STATIC_ENDPOINT}/${database}/${replayHash}.jsz`;
            const response = await fetch(url);
            const buffer = await response.arrayBuffer();

            return this.unzip(buffer) as ReplayData;
        }
        catch (e)
        {
            console.warn(`[APIPlugin] Fetch replay file failed.\n${e}`);

            return null;
        }
    }

    // === BUILT-IN DATABASE ==========================================================================================

    private localFetchLevelReplayEntries(levelHash: string, max: number): ReplayEntry[]
    {
        if (max > MAX_REPLAY_ENTRIES_FETCH) max = MAX_REPLAY_ENTRIES_FETCH;

        const allEntries = Shared.assets.data['entries'] as ReplayEntry[];

        if (!allEntries) return [];

        let levelEntries = allEntries.filter((entry) => entry.levelHash === levelHash);

        levelEntries = this.filterUniqueReplays(levelEntries);
        levelEntries = Random.shuffle(levelEntries);
        if (levelEntries.length > max)levelEntries.length = max;

        return levelEntries;
    }

    private localFetchLevelReplayFile(replayHash: string): ReplayData | null
    {
        try
        {
            return Shared.assets.data[replayHash] as ReplayData;
        }
        catch (e)
        {
            console.warn(`[APIPlugin] Fetch replay file failed.\n${e}`);

            return null;
        }
    }

    public fetchLocalReplays(levelHash: string, max: number): ReplayData[]
    {
        const entries = this.localFetchLevelReplayEntries(levelHash, max);
        const dataList: ReplayData[] = [];

        for (const entry of entries)
        {
            const replayData = this.localFetchLevelReplayFile(entry.replayHash);

            if (replayData) dataList.push(replayData);
        }

        return dataList;
    }

    // === FIREBASE ===================================================================================================
    /**
     * Initialises firebase
     * @returns
     */
    private firebaseInit(): void
    {
        if (this.firebaseInitialised) return;

        const firebaseConfig = {
            apiKey: 'AIzaSyBhDNGqJtI6JIexxs2soNKxf_1WOaVDH0A',
            authDomain: 'cn-toon-trails.firebaseapp.com',
            projectId: 'cn-toon-trails',
            storageBucket: 'cn-toon-trails.appspot.com',
            messagingSenderId: '292290863830',
            appId: '1:292290863830:web:28f980491bf429e2621f77',
        };

        firebase.initializeApp(firebaseConfig);
        this.firebaseInitialised = true;
        console.log('[ApiPlugin] Firebase - initialised');
    }

    private async firebaseSubmitReplay(entry: ReplayEntry, data: ReplayBinaryData): Promise<void>
    {
        this.firebaseInit();
        // UPLOAD REPLAY FILE
        // Use Firebase's Cloud Storage API to save binary data into a file
        const storageRef = firebase.storage().ref();
        const fileRef = storageRef.child(`replays/${entry.replayHash}.jsz`);

        try
        {
            const snapshot = await fileRef.put(data);

            console.log('[ApiPlugin] Firebase - Replay file uploaded. Snapshot:', snapshot);
        }
        catch (e)
        {
            console.warn(`[ApiPlugin] Firebase - Replay file upload failed.\n${e}`);

            // If api fails to upload the replay file, there is no reason to
            // push the replay entry. We can stop here
            return;
        }

        // REGISTER REPLAY ENTRY
        // Use Fiebase's Firestore database to register a replay entry
        const db = firebase.firestore();

        try
        {
            const ref = await db.collection('replays').add({
                ...entry,
                timestamp: firebase.firestore.FieldValue.serverTimestamp(),
            });

            console.log('[ApiPlugin] Firebase - Replay entry submited. Snapshot:', ref);
        }
        catch (e)
        {
            console.warn(`[ApiPlugin] Firebase - Replay entry submission failed.\n${e}`);

            return;
        }
    }

    /**
     * Fetch replay entries by level hash and sort them out, to be used as players in a race.
     * The ideal list of competitors would have:
     * - Good, regular and bad players, in similar shares
     * - Make it quite random in a way that each race will have a diffeent set of players
     * - Avoid repeated characters and no repeated replays
     * @param levelHash - Only entries with that level hash will be considered
     * @param max - Max amount of results, capped by absolute MAX_REPLAY_ENTRIES_FETCH
     * @returns
     */
    private async firebaseFetchLevelReplayEntries(levelHash: string, max: number): Promise<ReplayEntry[]>
    {
        this.firebaseInit();
        if (max > MAX_REPLAY_ENTRIES_FETCH) max = MAX_REPLAY_ENTRIES_FETCH;

        let entries = [] as ReplayEntry[];
        const systemVersion = Shared.game.level.LEVEL_SYSTEM_VERSION;

        try
        {
            console.log('[APIPlugin] Firebase - Fetch most recent replay entries');
            const db = firebase.firestore();
            const snapshot = await db.collection('replays')
                .where('systemVersion', '==', systemVersion) // System version should match (mandatory)
                .where('levelHash', '==', levelHash) // Level hash should match (mandatory)
                .orderBy('timestamp', 'desc') // Order by date, descending - newest first
                .limit(max * 2) // Get twice as much max players, so we can shuffle them
                .get();

            console.log('[APIPlugin] Firebase - Fetch most recent entries results:', snapshot.size);
            snapshot.forEach((doc) =>
            {
                const entry = doc.data() as any as ReplayEntry;

                entries.push(entry);
            });
        }
        catch (e)
        {
            console.warn(`[APIPlugin] Firebase - Fetch most recent entries failed.\n${e}`);
        }

        try
        {
            console.log('[APIPlugin] Fetch best replay entries');
            const db = firebase.firestore();
            const snapshot = await db.collection('replays')
                .where('systemVersion', '==', systemVersion) // System version should match (mandatory)
                .where('levelHash', '==', levelHash) // Level hash should match (mandatory)
                .orderBy('replayTime', 'asc') // Order by race time, ascending - fastest/lowest first
                .limit(max) // Fetch the same amount of max players, for balancing
                .get();

            console.log('[APIPlugin] Firebase - Fetch best entries results:', snapshot.size);
            snapshot.forEach((doc) =>
            {
                const entry = doc.data() as any as ReplayEntry;

                entries.push(entry);
            });
        }
        catch (e)
        {
            console.warn(`[APIPlugin] Firebase - Fetch best entries failed.\n${e}`);
        }

        // At this point we should have a list composed by 1/3 of top entries
        // and 2/3 of most recent entries, that can have good, bad and regular players.
        // Shuffle that, so we can pick random entries for the race
        entries = Random.shuffle(entries);

        // The list might have repeated entries at this point, so we have to filter out
        // entries with the same replay hash
        entries = this.filterUniqueReplays(entries);

        // Now that the list is randomised and unique, its length can be capped to
        // the max amount of results, if is bigger
        if (entries.length > max) entries.length = max;

        return entries;
    }

    /**
     * Download a replay file from given replay hash
     * @param replayHash - The unique id of a replay
     * @returns
     */
    private async firebaseFetchLevelReplayFile(replayHash: string): Promise<ReplayData | null>
    {
        this.firebaseInit();
        try
        {
            const storageRef = firebase.storage().ref();
            const url = await storageRef.child(`replays/${replayHash}.jsz`).getDownloadURL();
            const response = await fetch(url);
            const buffer = await response.arrayBuffer();

            return this.unzip(buffer) as ReplayData;
        }
        catch (e)
        {
            console.warn(`[APIPlugin] Firebase - Fetch replay file failed.\n${e}`);

            return null;
        }
    }

    // === UTILITY ====================================================================================================

    /**
     * Ensure unique replay enties, filtering out the ones with repeated replay hash
     * @param entries - Replay entry aay to be filtered
     * @returns
     */
    private filterUniqueReplays(entries: ReplayEntry[]): ReplayEntry[]
    {
        const flags = {} as Record<string, boolean>;
        const output = [] as ReplayEntry[];
        const l = entries.length;

        for (let i = 0; i < l; i++)
        {
            const entry = entries[i];

            if (flags[entry.replayHash]) continue;
            flags[entry.replayHash] = true;
            output.push(entry);
        }

        return output;
    }

    private unzip(buffer: ArrayBuffer): unknown
    {
        const json = pako.inflate(buffer, { to: 'string' });

        return JSON.parse(json);
    }
}
